import { useMemo, useState } from 'react';
import { noSelectionKey } from '../../../../../../utilities/filterAndSortingsUtil';
import {
  getAttr,
  getEntityId,
  getRelIds,
} from '../../../../../../features/helpers/apiHelpers';

export function getInvitationKeyDisplayValue(invitationKey) {
  if (getAttr(invitationKey, 'cpr')) return getAttr(invitationKey, 'cpr');
  if (getAttr(invitationKey, 'cvr')) return getAttr(invitationKey, 'cvr');
  if (getAttr(invitationKey, 'email')) return getAttr(invitationKey, 'email');
  return `Missing value - ID: ${getEntityId(invitationKey)}`;
}

export function getInvitationKeyType(invitationKey) {
  if (getAttr(invitationKey, 'cpr')) return 'CPR';
  if (getAttr(invitationKey, 'cvr')) return 'CVR';
  if (getAttr(invitationKey, 'email')) return 'Email';
  return 'Unknown';
}

function createGroupedData(invitationGroups, invitationKeys) {
  return invitationGroups.map((invitationGroup) => ({
    invitationGroup,
    invitationKeys: invitationKeys.filter((invitationKey) =>
      getRelIds(invitationGroup, 'invitationKeys').includes(
        getEntityId(invitationKey),
      ),
    ),
  }));
}

function transformForDetailsList(groupedData, titleFilter) {
  const flatItems = [];
  const groups = [];

  groupedData.forEach((groupData) => {
    const groupName = getAttr(groupData.invitationGroup, 'name');
    const groupId = getEntityId(groupData.invitationGroup);
    const invitationKeys = groupData.invitationKeys || [];

    const group = {
      // DetailList properties
      key: `group_${groupId}`,
      name: groupName,
      startIndex: flatItems.length,
      count: invitationKeys.length,
      level: 0,
      isCollapsed: !titleFilter,
      // Custom data
      data: { groupId },
    };

    invitationKeys.forEach((invitationKey) => {
      flatItems.push({
        // DetailList properties
        key: `${groupId}_${invitationKey.id}`,
        groupId: groupId,
        groupName: groupName,
        // Column properties
        displayValue: getInvitationKeyDisplayValue(invitationKey),
        type: getInvitationKeyType(invitationKey),
      });
    });

    groups.push(group);
  });

  return { items: flatItems, groups };
}

function filterItems(groups, items, filterText) {
  if (!filterText || filterText === noSelectionKey) {
    return { filteredGroups: groups, filteredItems: items };
  }

  const filteredItems = items.filter((item) =>
    item.displayValue.toLowerCase().includes(filterText.toLowerCase()),
  );

  let currentIndex = 0;
  const updatedGroups = groups.map((group) => {
    const groupItemCount = filteredItems.filter(
      (item) => item.groupId === group.data.groupId,
    ).length;

    const updatedGroup = {
      ...group,
      startIndex: currentIndex,
      count: groupItemCount,
    };

    currentIndex += groupItemCount;
    return updatedGroup;
  });

  return {
    filteredGroups: updatedGroups,
    filteredItems: filteredItems,
  };
}

function sortGroupsAlphabetically(groups, items) {
  const sortedGroups = [...groups].sort((a, b) =>
    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );

  const sortedItems = [];
  const updatedGroups = sortedGroups.map((group) => {
    const groupItems = items.filter(
      (item) => item.groupId === group.data.groupId,
    );
    const startIndex = sortedItems.length;
    const updatedGroup = {
      ...group,
      startIndex,
    };
    sortedItems.push(...groupItems);
    return updatedGroup;
  });

  return { sortedGroups: updatedGroups, sortedItems };
}

export const useFilterAndSort = (invitationGroups, invitationKeys) => {
  const [titleFilter, setTitleFilter] = useState(null);

  const { filteredAndSortedItems, groups } = useMemo(() => {
    if (!invitationGroups || !invitationKeys) {
      return { filteredAndSortedItems: [], groups: [] };
    }

    const groupedData = createGroupedData(invitationGroups, invitationKeys);
    const detailsListData = transformForDetailsList(groupedData, titleFilter);

    const { filteredGroups, filteredItems } = filterItems(
      detailsListData.groups,
      detailsListData.items,
      titleFilter,
    );

    const { sortedGroups, sortedItems } = sortGroupsAlphabetically(
      filteredGroups,
      filteredItems,
    );

    return {
      filteredAndSortedItems: sortedItems,
      groups: sortedGroups,
    };
  }, [invitationGroups, invitationKeys, titleFilter]);

  const handleTitleFilterChange = (value) => {
    setTitleFilter(value);
  };

  return {
    titleFilter,
    filteredAndSortedItems,
    groups,
    handleTitleFilterChange,
  };
};
